<template>
  <div class="alerts-manager">
    <!-- Header avec statistiques -->
    <div class="alerts-header">
      <h3 class="alerts-title">
        <i class="fas fa-exclamation-triangle"></i>
        Alertes et Notifications
      </h3>
      <div class="alerts-stats">
        <div class="stat-badge critical">
          <span class="count">{{ criticalAlerts }}</span>
          <span class="label">Critiques</span>
        </div>
        <div class="stat-badge warning">
          <span class="count">{{ warningAlerts }}</span>
          <span class="label">Avertissements</span>
        </div>
        <div class="stat-badge info">
          <span class="count">{{ infoAlerts }}</span>
          <span class="label">Informations</span>
        </div>
      </div>
    </div>

    <!-- Filtres -->
    <div class="alerts-filters">
      <div class="filter-group">
        <label class="filter-label">Filtrer par type</label>
        <div class="type-selector-wrapper" :class="{ 'has-selection': filters.type }">
          <div class="type-selector-icon">
            <i class="fas fa-filter"></i>
          </div>
          <select v-model="filters.type" @change="applyFilters" class="filter-select">
            <option value="">🔍 Tous les types</option>
            <option value="stock_bas">📦 Stock bas</option>
            <option value="sim_non_activee">📱 SIM non activée</option>
            <option value="materiel_en_retard">⏰ Matériel en retard</option>
            <option value="bon_non_signe">📝 Bon non signé</option>
          </select>
          <div class="type-selector-arrow">
            <i class="fas fa-chevron-down"></i>
          </div>
          <div v-if="filters.type" class="type-selection-indicator">
            <i class="fas fa-check-circle"></i>
          </div>
          <div v-if="filters.type" class="type-selection-badge">1</div>
        </div>
      </div>
      
      <div class="filter-group">
        <label class="filter-label">Filtrer par priorité</label>
        <div class="priority-selector-wrapper" :class="{ 'has-selection': filters.priorite }">
          <div class="priority-selector-icon">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <select v-model="filters.priorite" @change="applyFilters" class="filter-select">
            <option value="">⚡ Toutes priorités</option>
            <option value="critique">🔴 Critique</option>
            <option value="avertissement">🟠 Avertissement</option>
            <option value="information">🔵 Information</option>
          </select>
          <div class="priority-selector-arrow">
            <i class="fas fa-chevron-down"></i>
          </div>
          <div v-if="filters.priorite" class="priority-selection-indicator">
            <i class="fas fa-check-circle"></i>
          </div>
          <div v-if="filters.priorite" class="priority-selection-badge">1</div>
        </div>
      </div>
      
      <button @click="markAllAsRead" class="btn btn-secondary">
        <i class="fas fa-check-double"></i>
        Tout marquer comme lu
      </button>
    </div>

    <!-- Liste des alertes -->
    <div class="alerts-list">
      <div v-if="loading" class="loading-overlay">
        <div class="loading-spinner">
          <i class="fas fa-spinner fa-spin"></i>
          <span>Chargement des alertes...</span>
        </div>
      </div>
      
      <div v-else-if="filteredAlerts.length === 0" class="no-alerts">
        <i class="fas fa-check-circle"></i>
        <h3>Aucune alerte</h3>
        <p>Tout semble en ordre ! Aucune alerte à signaler.</p>
      </div>
      
      <div v-else class="alerts-container">
        <!-- Alertes Stock Bas -->
        <div v-if="stockAlerts.length > 0" class="alert-section">
          <h4 class="section-title">
            <i class="fas fa-boxes"></i>
            Stock Bas ({{ stockAlerts.length }})
          </h4>
          <div class="alert-cards">
            <div v-for="alert in stockAlerts" :key="alert._id" class="alert-card stock-alert">
              <div class="alert-icon">
                <i class="fas fa-exclamation-triangle"></i>
              </div>
              <div class="alert-content">
                <div class="alert-title">Stock critique</div>
                <div class="alert-message">
                  <strong>{{ alert.designation }}</strong> ({{ alert.referenceInterne }})
                </div>
                <div class="alert-details">
                  Stock actuel: {{ alert.stockActuel }} | Seuil: {{ alert.seuilAlerte }}
                </div>
              </div>
              <div class="alert-actions">
                <button @click="resolveAlert(alert)" class="btn-action btn-resolve">
                  <i class="fas fa-check"></i>
                </button>
                <button @click="viewProduct(alert)" class="btn-action btn-view">
                  <i class="fas fa-eye"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Alertes SIM non activées -->
        <div v-if="simAlerts.length > 0" class="alert-section">
          <h4 class="section-title">
            <i class="fas fa-sim-card"></i>
            SIM Non Activées ({{ simAlerts.length }})
          </h4>
          <div class="alert-cards">
            <div v-for="alert in simAlerts" :key="alert._id" class="alert-card sim-alert">
              <div class="alert-icon">
                <i class="fas fa-clock"></i>
              </div>
              <div class="alert-content">
                <div class="alert-title">SIM non activée</div>
                <div class="alert-message">
                  <strong>{{ alert.designation }}</strong>
                </div>
                <div class="alert-details">
                  ICCID: {{ alert.iccid }} | En stock depuis: {{ getDaysInStock(alert.dateCreation) }} jours
                </div>
              </div>
              <div class="alert-actions">
                <button @click="activateSim(alert)" class="btn-action btn-activate">
                  <i class="fas fa-play"></i>
                </button>
                <button @click="viewProduct(alert)" class="btn-action btn-view">
                  <i class="fas fa-eye"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Alertes Matériel en retard -->
        <div v-if="retardAlerts.length > 0" class="alert-section">
          <h4 class="section-title">
            <i class="fas fa-user-clock"></i>
            Matériel en Retard ({{ retardAlerts.length }})
          </h4>
          <div class="alert-cards">
            <div v-for="alert in retardAlerts" :key="alert._id" class="alert-card retard-alert">
              <div class="alert-icon">
                <i class="fas fa-exclamation-circle"></i>
              </div>
              <div class="alert-content">
                <div class="alert-title">Retour en retard</div>
                <div class="alert-message">
                  Bon {{ alert.numeroBon }} - {{ alert.technicien?.prenom }} {{ alert.technicien?.nom }}
                </div>
                <div class="alert-details">
                  Retour prévu: {{ formatDate(alert.dateRetourPrevue) }} | 
                  Retard: {{ getDaysLate(alert.dateRetourPrevue) }} jours
                </div>
              </div>
              <div class="alert-actions">
                <button @click="contactTechnician(alert)" class="btn-action btn-contact">
                  <i class="fas fa-phone"></i>
                </button>
                <button @click="viewBon(alert)" class="btn-action btn-view">
                  <i class="fas fa-eye"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'

export default {
  name: 'AlertsManager',
  props: {
    alerts: {
      type: Object,
      default: () => ({
        alertesSeuil: [],
        simSansActivation: [],
        materielsEnRetard: []
      })
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['refresh', 'resolve', 'activate', 'contact', 'view-product', 'view-bon'],
  setup(props, { emit }) {
    const filters = ref({
      type: '',
      priorite: ''
    })

    // Alertes par catégorie
    const stockAlerts = computed(() => props.alerts.alertesSeuil || [])
    const simAlerts = computed(() => props.alerts.simSansActivation || [])
    const retardAlerts = computed(() => props.alerts.materielsEnRetard || [])

    // Toutes les alertes combinées
    const allAlerts = computed(() => [
      ...stockAlerts.value.map(alert => ({ ...alert, type: 'stock_bas', priorite: 'critique' })),
      ...simAlerts.value.map(alert => ({ ...alert, type: 'sim_non_activee', priorite: 'avertissement' })),
      ...retardAlerts.value.map(alert => ({ ...alert, type: 'materiel_en_retard', priorite: 'critique' }))
    ])

    // Alertes filtrées
    const filteredAlerts = computed(() => {
      let result = [...allAlerts.value]
      
      if (filters.value.type) {
        result = result.filter(alert => alert.type === filters.value.type)
      }
      
      if (filters.value.priorite) {
        result = result.filter(alert => alert.priorite === filters.value.priorite)
      }
      
      return result
    })

    // Statistiques
    const criticalAlerts = computed(() => 
      allAlerts.value.filter(alert => alert.priorite === 'critique').length
    )
    
    const warningAlerts = computed(() => 
      allAlerts.value.filter(alert => alert.priorite === 'avertissement').length
    )
    
    const infoAlerts = computed(() => 
      allAlerts.value.filter(alert => alert.priorite === 'information').length
    )

    // Méthodes
    const applyFilters = () => {
      // Les filtres sont appliqués automatiquement via computed
    }

    const markAllAsRead = () => {
      // TODO: Implémenter la logique pour marquer toutes les alertes comme lues
      console.log('Marquer toutes les alertes comme lues')
    }

    const resolveAlert = (alert) => {
      emit('resolve', alert)
    }

    const activateSim = (alert) => {
      emit('activate', alert)
    }

    const contactTechnician = (alert) => {
      emit('contact', alert)
    }

    const viewProduct = (alert) => {
      emit('view-product', alert)
    }

    const viewBon = (alert) => {
      emit('view-bon', alert)
    }

    const getDaysInStock = (dateCreation) => {
      const now = new Date()
      const created = new Date(dateCreation)
      const diffTime = Math.abs(now - created)
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    }

    const getDaysLate = (dateRetourPrevue) => {
      const now = new Date()
      const prevue = new Date(dateRetourPrevue)
      const diffTime = Math.abs(now - prevue)
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    }

    const formatDate = (date) => {
      return new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(new Date(date))
    }

    return {
      filters,
      stockAlerts,
      simAlerts,
      retardAlerts,
      allAlerts,
      filteredAlerts,
      criticalAlerts,
      warningAlerts,
      infoAlerts,
      applyFilters,
      markAllAsRead,
      resolveAlert,
      activateSim,
      contactTechnician,
      viewProduct,
      viewBon,
      getDaysInStock,
      getDaysLate,
      formatDate
    }
  }
}
</script>

<style scoped>
.alerts-manager {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.alerts-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
  color: white;
  flex-wrap: wrap;
  gap: 1rem;
}

.alerts-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.25rem;
  font-weight: 600;
}

.alerts-stats {
  display: flex;
  gap: 1rem;
}

.stat-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  min-width: 70px;
}

.stat-badge.critical {
  border-left: 4px solid #dc3545;
}

.stat-badge.warning {
  border-left: 4px solid #ffc107;
}

.stat-badge.info {
  border-left: 4px solid #17a2b8;
}

.stat-badge .count {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
}

.stat-badge .label {
  font-size: 0.75rem;
  opacity: 0.9;
  margin-top: 0.25rem;
}

/* Filtres Ultra-Professionnels */
.alerts-filters {
  display: flex;
  gap: 2rem;
  padding: 2rem 1.5rem;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95));
  backdrop-filter: blur(15px);
  border-bottom: 2px solid rgba(0, 27, 183, 0.08);
  flex-wrap: wrap;
  align-items: flex-end;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 27, 183, 0.06);
}

.alerts-filters::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.02), rgba(0, 70, 255, 0.01));
  z-index: 0;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
  flex: 1;
  min-width: 250px;
}

.filter-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--primary-blue);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
  opacity: 0.9;
}

/* Type Selector Ultra-Professional Design */
.type-selector-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95));
  backdrop-filter: blur(15px);
  border: 2px solid rgba(0, 27, 183, 0.12);
  border-radius: 16px;
  padding: 0;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 8px 32px rgba(0, 27, 183, 0.06);
  overflow: hidden;
  min-height: 52px;
}

.type-selector-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.02), rgba(0, 70, 255, 0.01));
  z-index: 0;
}

.type-selector-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 27, 183, 0.12);
  border-color: rgba(0, 27, 183, 0.2);
}

.type-selector-wrapper:focus-within {
  transform: translateY(-3px);
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 4px rgba(0, 27, 183, 0.08), 0 15px 45px rgba(0, 27, 183, 0.15);
}

.type-selector-wrapper:focus-within .type-selector-arrow i {
  transform: rotate(180deg);
}

.type-selector-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border-radius: 12px;
  margin: 2px;
  font-size: 1rem;
  position: relative;
  z-index: 2;
  box-shadow: 0 4px 16px rgba(0, 27, 183, 0.25);
  transition: all 0.3s ease;
}

.type-selector-wrapper.has-selection .type-selector-icon {
  background: linear-gradient(135deg, #10b981, #059669);
  animation: pulse 2s infinite;
}

.type-selector-wrapper .filter-select {
  flex: 1;
  background: transparent !important;
  border: none !important;
  padding: 0.75rem 1rem !important;
  font-size: 0.85rem;
  color: var(--primary-blue);
  font-weight: 600;
  position: relative;
  z-index: 1;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.type-selector-wrapper .filter-select:focus {
  outline: none;
  box-shadow: none !important;
  transform: none !important;
  border: none !important;
}

.type-selector-wrapper.has-selection .filter-select {
  color: var(--secondary-blue);
  font-weight: 700;
}

.type-selector-wrapper .filter-select option {
  background: white;
  color: #333;
  padding: 0.75rem;
  font-size: 0.85rem;
  border-radius: 8px;
  margin: 2px 0;
}

.type-selector-wrapper .filter-select option:first-child {
  color: #6c757d;
  font-style: italic;
}

.type-selector-wrapper .filter-select option:not(:first-child) {
  font-weight: 600;
}

.type-selector-wrapper .filter-select option:hover {
  background: linear-gradient(135deg, rgba(0, 27, 183, 0.05), rgba(0, 70, 255, 0.02));
}

.type-selector-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: var(--primary-blue);
  font-size: 0.9rem;
  position: relative;
  z-index: 2;
  margin-right: 6px;
  transition: all 0.3s ease;
}

.type-selector-arrow i {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.type-selector-wrapper.has-selection .type-selector-arrow {
  color: var(--secondary-blue);
}

.type-selection-indicator {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.7rem;
  z-index: 3;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  animation: pulse 2s infinite;
}

.type-selection-badge {
  position: absolute;
  top: -6px;
  right: 8px;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  z-index: 4;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
  animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

/* Priority Selector Ultra-Professional Design */
.priority-selector-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95));
  backdrop-filter: blur(15px);
  border: 2px solid rgba(255, 152, 0, 0.12);
  border-radius: 16px;
  padding: 0;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 8px 32px rgba(255, 152, 0, 0.06);
  overflow: hidden;
  min-height: 52px;
}

.priority-selector-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.02), rgba(255, 193, 7, 0.01));
  z-index: 0;
}

.priority-selector-wrapper:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(255, 152, 0, 0.12);
  border-color: rgba(255, 152, 0, 0.2);
}

.priority-selector-wrapper:focus-within {
  transform: translateY(-3px);
  border-color: #ff9800;
  box-shadow: 0 0 0 4px rgba(255, 152, 0, 0.08), 0 15px 45px rgba(255, 152, 0, 0.15);
}

.priority-selector-wrapper:focus-within .priority-selector-arrow i {
  transform: rotate(180deg);
}

.priority-selector-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #ff9800, #f57c00);
  color: white;
  border-radius: 12px;
  margin: 2px;
  font-size: 1rem;
  position: relative;
  z-index: 2;
  box-shadow: 0 4px 16px rgba(255, 152, 0, 0.25);
  transition: all 0.3s ease;
}

.priority-selector-wrapper.has-selection .priority-selector-icon {
  background: linear-gradient(135deg, #10b981, #059669);
  animation: pulse 2s infinite;
}

.priority-selector-wrapper .filter-select {
  flex: 1;
  background: transparent !important;
  border: none !important;
  padding: 0.75rem 1rem !important;
  font-size: 0.85rem;
  color: #ff9800;
  font-weight: 600;
  position: relative;
  z-index: 1;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.priority-selector-wrapper .filter-select:focus {
  outline: none;
  box-shadow: none !important;
  transform: none !important;
  border: none !important;
}

.priority-selector-wrapper.has-selection .filter-select {
  color: #f57c00;
  font-weight: 700;
}

.priority-selector-wrapper .filter-select option {
  background: white;
  color: #333;
  padding: 0.75rem;
  font-size: 0.85rem;
  border-radius: 8px;
  margin: 2px 0;
  font-weight: 600;
}

.priority-selector-wrapper .filter-select option:hover {
  background: linear-gradient(135deg, rgba(255, 152, 0, 0.05), rgba(255, 193, 7, 0.02));
}

.priority-selector-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: #ff9800;
  font-size: 0.9rem;
  position: relative;
  z-index: 2;
  margin-right: 6px;
  transition: all 0.3s ease;
}

.priority-selector-arrow i {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.priority-selector-wrapper.has-selection .priority-selector-arrow {
  color: #f57c00;
}

.priority-selection-indicator {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.7rem;
  z-index: 3;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  animation: pulse 2s infinite;
}

.priority-selection-badge {
  position: absolute;
  top: -6px;
  right: 8px;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #dc3545, #c82333);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  z-index: 4;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
  animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

@keyframes bounceIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Hover effect with shine animation */
.type-selector-wrapper:hover::after,
.priority-selector-wrapper:hover::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: shine 0.8s ease-out;
  z-index: 1;
}

@keyframes shine {
  0% { left: -100%; }
  100% { left: 100%; }
}

.alerts-list {
  position: relative;
  padding: 1.5rem;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: #007bff;
}

.no-alerts {
  text-align: center;
  padding: 4rem 2rem;
  color: #28a745;
}

.no-alerts i {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.7;
}

.no-alerts h3 {
  margin-bottom: 0.5rem;
  color: #495057;
}

.alert-section {
  margin-bottom: 2rem;
}

.alert-section:last-child {
  margin-bottom: 0;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

.alert-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.alert-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.alert-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
}

.stock-alert::before {
  background: #dc3545;
}

.sim-alert::before {
  background: #ffc107;
}

.retard-alert::before {
  background: #fd7e14;
}

.alert-card:hover {
  transform: translateX(4px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.alert-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.stock-alert .alert-icon {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.sim-alert .alert-icon {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
}

.retard-alert .alert-icon {
  background: rgba(253, 126, 20, 0.1);
  color: #fd7e14;
}

.alert-content {
  flex: 1;
  min-width: 0;
}

.alert-title {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.25rem;
}

.alert-message {
  color: #212529;
  margin-bottom: 0.25rem;
}

.alert-details {
  font-size: 0.85rem;
  color: #6c757d;
  line-height: 1.4;
}

.alert-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.btn-action {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.btn-resolve { background: #28a745; color: white; }
.btn-activate { background: #007bff; color: white; }
.btn-contact { background: #17a2b8; color: white; }
.btn-view { background: #6c757d; color: white; }

.btn-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

@media (max-width: 768px) {
  .alerts-header {
    flex-direction: column;
    text-align: center;
  }
  
  .alerts-stats {
    justify-content: center;
  }
  
  .alerts-filters {
    flex-direction: column;
    align-items: stretch;
    gap: 1.5rem;
    padding: 1.5rem;
  }
  
  .filter-group {
    min-width: auto;
    width: 100%;
  }
  
  .type-selector-wrapper,
  .priority-selector-wrapper {
    min-height: 48px;
  }
  
  .type-selector-icon,
  .priority-selector-icon {
    width: 44px;
    height: 44px;
    font-size: 0.9rem;
  }
  
  .type-selector-wrapper .filter-select,
  .priority-selector-wrapper .filter-select {
    font-size: 0.8rem;
    padding: 0.6rem 0.8rem !important;
  }
  
  .type-selector-arrow,
  .priority-selector-arrow {
    width: 36px;
    height: 36px;
    font-size: 0.8rem;
  }
  
  .type-selection-indicator,
  .priority-selection-indicator {
    width: 20px;
    height: 20px;
    font-size: 0.6rem;
    top: -6px;
    right: -6px;
  }
  
  .type-selection-badge,
  .priority-selection-badge {
    width: 18px;
    height: 18px;
    font-size: 0.6rem;
    top: -4px;
    right: 6px;
  }
  
  .alert-card {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  
  .alert-actions {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .alerts-filters {
    padding: 1rem;
    gap: 1rem;
  }
  
  .type-selector-wrapper,
  .priority-selector-wrapper {
    min-height: 44px;
    border-radius: 12px;
  }
  
  .type-selector-icon,
  .priority-selector-icon {
    width: 40px;
    height: 40px;
    font-size: 0.85rem;
    border-radius: 10px;
  }
  
  .type-selector-wrapper .filter-select,
  .priority-selector-wrapper .filter-select {
    font-size: 0.75rem;
    padding: 0.5rem 0.75rem !important;
  }
  
  .type-selector-arrow,
  .priority-selector-arrow {
    width: 32px;
    height: 32px;
    font-size: 0.75rem;
    margin-right: 4px;
  }
  
  .filter-label {
    font-size: 0.7rem;
  }
}
</style>
